import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, Upload, CheckCircle2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';

export const GCCRegionPage = () => {
  const countries = [
    { name: 'Bahrain', slug: 'bahrain-pharma-nutra-registration' },
    { name: 'Kuwait', slug: 'kuwait' },
    { name: 'Oman', slug: 'oman-pharma-nutra-registration' },
    { name: 'Qatar', slug: 'qatar-pharma-nutra-registration' },
    { name: 'Saudi Arabia', slug: 'saudi-arabia-pharma-nutra-registration' },
    { name: 'UAE', slug: 'united-arab-emirates-pharma-nutra-registration' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>GCC Pharma Registration | Saudi Arabia UAE CTD eCTD Dossier SFDA Qatar Kuwait</title>
        <meta
          name="description"
          content="Expert CTD/eCTD dossier services for pharmaceutical and nutraceutical registration in GCC countries. Saudi SFDA, UAE MOH, Qatar, Kuwait, Bahrain, Oman compliance."
        />
        <meta name="keywords" content="GCC pharma registration, saudi arabia SFDA, UAE pharma registration, qatar dossier, kuwait registration, bahrain pharma, oman regulatory, CTD dossier GCC, eCTD saudi, arabic artwork compliance, GCC regulatory affairs" />
        
        <meta property="og:title" content="GCC Pharma Registration | Saudi Arabia UAE Qatar Kuwait Regulatory Affairs" />
        <meta property="og:description" content="Compliance-first strategy for high-standard GCC markets including Saudi Arabia, UAE, Qatar, Kuwait, Bahrain and Oman." />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6" style={{ backgroundColor: '#A060B0', color: 'white' }}>
            GCC Region
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            Regulatory Affairs in the GCC
          </h1>
          <p className="text-2xl mb-4" style={{ color: '#505060' }}>
            Compliance-first strategy for high-standard markets like Saudi Arabia, UAE, Qatar, Kuwait, Bahrain & Oman.
          </p>
          <p className="text-xl mb-12" style={{ color: '#606070' }}>
            We help manufacturers meet GCC's stringent dossier, artwork, GMP and importer requirements — without delays or rejections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('countries').scrollIntoView({ behavior: 'smooth' })} style={{ backgroundColor: '#A060B0', color: 'white' }} className="text-lg px-8 py-6">
              Explore GCC Markets
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" style={{ borderColor: '#A060B0', color: '#A060B0' }}>
                Speak to a Regulatory Specialist
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why GCC Matters */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why GCC Matters
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            The GCC is one of the world's most lucrative pharma & nutra markets:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'High per-capita income',
              'Strong demand for imported products',
              'Rapidly growing chronic-care segment',
              'High regulatory expectations',
              'Mandatory GMP, stability & CoPP requirements'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: '#F0E0D0' }}>
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#A060B0' }} />
                <span className="text-lg" style={{ color: '#303040' }}>{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xl mt-8 text-center font-medium" style={{ color: '#A060B0' }}>
            GCC registrations build premium credibility for your global expansion.
          </p>
        </div>
      </section>

      {/* Regulatory Landscape */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Regulatory Landscape (GCC)
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>CTD Format</h3>
              <p style={{ color: '#505060' }}>CTD dossier format widely accepted across all GCC markets</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>Arabic Required</h3>
              <p style={{ color: '#505060' }}>Arabic translations mandatory. UAE, Saudi & Qatar have strict artwork/labeling rules</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>Distributor System</h3>
              <p style={{ color: '#505060' }}>Distributor-based submission systems across GCC</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>Saudi eCTD</h3>
              <p style={{ color: '#505060' }}>Saudi Arabia has additional requirements (SFDA eCTD, pharmacovigilance)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Countries Covered */}
      <section id="countries" className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Countries Covered
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {countries.map((country) => (
              <Link
                key={country.slug}
                to={`/country/${country.slug}`}
                className="group block p-6 bg-white rounded-2xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                style={{ borderLeft: '4px solid #A060B0' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold" style={{ color: '#303040' }}>{country.name}</span>
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-2" style={{ color: '#A060B0' }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            What TheApostrophe Does in the GCC
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'CTD/eCTD dossier development',
              'Arabic artwork & label adaptation',
              'GMP, CPP & CoA alignment',
              'Importer coordination and MoH submission readiness',
              'Deficiency/query handling',
              'Compliance for supplements & semi-regulated products'
            ].map((service, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#A060B0' }} />
                <span className="text-lg" style={{ color: '#505060' }}>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Products */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Ideal Product Categories
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>Pharma</h3>
              <ul className="space-y-2">
                {['High-quality generics', 'Chronic-care therapies', 'Specialized formulations', 'Pediatric products'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#A060B0' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#A060B0' }}>Nutra</h3>
              <ul className="space-y-2">
                {['Cardio-metabolic support', 'Beauty-from-within formulations', 'Vitamins/mineral blends', 'Premium nutraceuticals'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#A060B0' }} />
                    <span style={{ color: '#505060' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#303040' }}>
              GCC Registration FAQs
            </h2>
            <p className="text-lg" style={{ color: '#708080' }}>
              Common questions about pharmaceutical registration in GCC countries
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="faq-1" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  Is Arabic translation mandatory for all GCC countries?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  Yes. <strong>All GCC countries</strong> (Saudi Arabia, UAE, Qatar, Kuwait, Bahrain, Oman) require Arabic translations for pharmaceutical labels and package inserts. The translation must be regulatory-grade with proper pharmaceutical terminology. We provide certified Arabic translations aligned with each country's MOH requirements.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-2" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  What is eCTD and is it required in Saudi Arabia?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>eCTD (Electronic Common Technical Document)</strong> is a digital dossier format. <strong>Saudi Arabia SFDA</strong> has transitioned to eCTD submissions through their online portal. We prepare eCTD-compliant dossiers with proper XML backbone and electronic signatures for SFDA submissions. Other GCC countries still accept paper/PDF CTD formats.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq-3" className="border-0 rounded-2xl overflow-hidden bg-white shadow-md">
              <AccordionTrigger className="px-6 py-5 hover:no-underline text-left">
                <span className="text-base font-bold pr-6" style={{ color: '#303040' }}>
                  What are typical GCC registration timelines?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-5">
                <p className="text-base leading-relaxed" style={{ color: '#708080' }}>
                  <strong>Bahrain and Oman:</strong> 6-12 months. <strong>Qatar and Kuwait:</strong> 9-15 months. <strong>UAE and Saudi Arabia:</strong> 12-24 months depending on product complexity. GCC timelines are longer due to rigorous review but result in premium market access. Complete dossiers with all technical data accelerate approvals.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #A060B0 0%, #B070C0 100%)' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Expand into one of the world's most regulated and rewarding regions
          </h2>
          <p className="text-2xl mb-8 text-white/90">
            Launch with confidence using our GCC-aligned dossier and compliance strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" className="text-lg px-8 py-6 bg-white hover:bg-white/90" style={{ color: '#A060B0' }}>
                Start GCC Expansion
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10">
                <Upload className="mr-2 h-5 w-5" />
                Upload Product List
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
